import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageMeta from "@/components/PageMeta";

/**
 * ActivateFamily — public landing page from the invite email.
 * Flow:
 *   1. Pull `code` from the URL and validate it via the public info endpoint.
 *   2. If the viewer isn't signed in → send them to /register (then back here).
 *   3. If signed in → single-click "Accept" to attach the account.
 */
export default function ActivateFamily() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").trim().toUpperCase();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!code) { setErr("No invite code in the URL."); setLoading(false); return; }
    api.get(`/family-plan/invite-info/${code}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This invite isn't valid."))
      .finally(() => setLoading(false));
  }, [code]);

  const accept = async () => {
    if (!user) {
      // Preserve the code through signup so the user lands here again.
      nav(`/register?next=${encodeURIComponent(`/activate-family?code=${code}`)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/family-plan/activate", { code });
      setDone(data);
      await refresh?.();
      toast.success("You're now on the Family Plan!");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Activation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="activate-family-page">
      <PageMeta title="Activate your Family Plan invitation" noindex />
      <div className="bg-white rounded-3xl border border-rf-ink/10 p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-4">
          <Users size={28} weight="duotone" className="text-rf-orange" />
          <h1 className="font-display text-3xl font-black text-rf-ink">Family Plan invitation</h1>
        </div>

        {loading && <p className="text-rf-ink-soft">Checking your invite…</p>}

        {!loading && err && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-5 text-red-900" data-testid="activate-family-error">
            <p className="font-bold mb-1">We can&apos;t use this code.</p>
            <p className="text-sm">{err}</p>
            <p className="text-sm mt-3">Ask the plan owner to send a fresh invite, or {" "}
              <Link to="/pricing" className="font-bold underline">buy your own subscription</Link>.
            </p>
          </div>
        )}

        {!loading && !err && done && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-900" data-testid="activate-family-success">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={22} weight="fill" className="text-emerald-600" />
              <p className="font-bold text-lg">You&apos;re in!</p>
            </div>
            <p className="text-sm">Your Family Plan membership is active until <strong>{new Date(done.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong>. Head to your <Link to="/dashboard" className="font-bold underline">dashboard</Link> to start scanning recipes.</p>
          </div>
        )}

        {!loading && !err && !done && info && (
          <div data-testid="activate-family-prompt">
            <p className="text-rf-ink leading-relaxed mb-4">
              <strong>{info.owner_name}</strong> has invited <strong>{info.invitee_name}</strong> ({info.invitee_email}) to join their Recipe Filer Family Plan.
            </p>
            <p className="text-sm text-rf-ink-soft mb-6">
              Accepting attaches your account to this plan with the same perks as a 12-month subscription, until <strong>{new Date(info.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong>. The plan owner handles renewals — you don&apos;t pay anything.
            </p>
            {!user && (
              <div className="rounded-xl bg-rf-cream-alt p-4 text-sm text-rf-ink-soft mb-4">
                You&apos;ll need to sign in or create an account first. Use the email <strong>{info.invitee_email}</strong> so your membership links correctly.
              </div>
            )}
            <button
              type="button" onClick={accept} disabled={busy}
              data-testid="activate-family-accept"
              className="btn-amber !py-3 !px-6 text-sm disabled:opacity-50"
            >
              {busy ? "Activating…" : (user ? "Accept invitation" : "Sign in / register to accept")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
